import { useCallback } from 'react';

import { useToast } from 'hooks/useToast';
import { extractErrorMessage } from 'utils/errors';

const TOAST_DEBOUNCE_MS = 2000;

type ToastStatus = 'success' | 'error' | 'warning' | 'info';

/**
 * Singleton manager for debouncing toasts across all hook/component instances.
 * This allowsus to prevent showing duplicate toasts within a short period of time.
 */
class ToastDebouncer {
  private recentToasts = new Map<string, number>();

  private cleanupTimers = new Map<string, NodeJS.Timeout>();

  // Track custom debounce keys that should prevent toasts from displaying again
  private customDebounceKeys = new Set<string>();

  /**
   * Create a unique key for a toast based on its message and status
   */
  private getToastKey(message: string, status: ToastStatus): string {
    return `${status}:${message}`;
  }

  /**
   * Check if a toast should be shown based on time-based debouncing
   */
  private shouldShowToastByTime(toastKey: string): boolean {
    const now = Date.now();
    const lastShownTime = this.recentToasts.get(toastKey);

    if (lastShownTime && now - lastShownTime < TOAST_DEBOUNCE_MS) {
      return false;
    }

    return true;
  }

  /**
   * Check if a toast should be shown based on custom debounce key
   */
  private shouldShowToastByKey(debounceKey: string | undefined): boolean {
    if (!debounceKey) {
      return true; // No custom key, allow toast
    }
    return !this.customDebounceKeys.has(debounceKey);
  }

  shouldShowToast(message: string, status: ToastStatus, debounceKey?: string): boolean {
    // If custom debounce key is provided, check it first
    if (debounceKey && !this.shouldShowToastByKey(debounceKey)) {
      return false;
    }

    const toastKey = this.getToastKey(message, status);
    return this.shouldShowToastByTime(toastKey);
  }

  recordToast(message: string, status: ToastStatus, debounceKey?: string): void {
    const now = Date.now();
    const toastKey = this.getToastKey(message, status);
    this.recentToasts.set(toastKey, now);

    // If custom debounce key is provided, mark it as shown
    if (debounceKey) {
      this.customDebounceKeys.add(debounceKey);
    }

    // Clear existing timer for this toast if any
    const existingTimer = this.cleanupTimers.get(toastKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set a timer to clean up this entry after the debounce period
    const timer = setTimeout(() => {
      this.recentToasts.delete(toastKey);
      this.cleanupTimers.delete(toastKey);
    }, TOAST_DEBOUNCE_MS);

    this.cleanupTimers.set(toastKey, timer);
  }

  /**
   * Clear a custom debounce key, allowing toasts with that key to be shown again
   */
  clearDebounceKey(debounceKey: string): void {
    this.customDebounceKeys.delete(debounceKey);
  }
}

// Singleton instance
const debouncer = new ToastDebouncer();

export function useDebouncedToast() {
  const toast = useToast();

  const showDebouncedToast = useCallback(
    (message: string, status: ToastStatus = 'info', debounceKey?: string) => {
      if (!debouncer.shouldShowToast(message, status, debounceKey)) {
        return;
      }

      // Show the toast
      toast({
        description: message,
        status,
      });

      // Record this toast as shown
      debouncer.recordToast(message, status, debounceKey);
    },
    [toast],
  );

  const showDebouncedErrorToast = useCallback(
    (error: any, baseMessage?: string, debounceKey?: string) => {
      const errorMessage = extractErrorMessage(error, baseMessage);
      showDebouncedToast(errorMessage, 'error', debounceKey);
    },
    [showDebouncedToast],
  );

  const showDebouncedSuccessToast = useCallback(
    (message: string) => {
      showDebouncedToast(message, 'success');
    },
    [showDebouncedToast],
  );

  const showDebouncedWarningToast = useCallback(
    (message: string) => {
      showDebouncedToast(message, 'warning');
    },
    [showDebouncedToast],
  );

  const showDebouncedInfoToast = useCallback(
    (message: string) => {
      showDebouncedToast(message, 'info');
    },
    [showDebouncedToast],
  );

  const clearDebounceKey = useCallback((debounceKey: string) => {
    debouncer.clearDebounceKey(debounceKey);
  }, []);

  return {
    showDebouncedToast,
    showDebouncedErrorToast,
    showDebouncedSuccessToast,
    showDebouncedWarningToast,
    showDebouncedInfoToast,
    clearDebounceKey,
  };
}
